import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useToastContext } from "components/alert/ToastContext";
import Base from "components/Base";
import { useFilter } from "contexts/FilterContext";
import { BaseModel } from "model/BaseModel";
import Pageable from "model/page/Pageable";
import Mdfe from "model/transportes/mdfe/Mdfe";
import MdfeCondutor from "model/transportes/mdfe/MdfeCondutor";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableFilterMeta, DataTableStateEvent } from "primereact/datatable";
import { FileUpload, FileUploadHandlerEvent } from "primereact/fileupload";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MdfeService from "service/transportes/MdfeService";
import { filterApplyTemplate, TextMatchModeOptions } from "utils/filterUtils";

const mdfeService = new MdfeService;

export default function MdfeListagem() {

    const [queryParameters] = useSearchParams();
    const navigate = useNavigate();
    const tituloPagina = 'MDFE';

    const [filtrando, setFiltrando] = useState<boolean>(false);
    const [rows, setRows] = useState<Mdfe[]>([]);
    const [pageable, setPageable] = useState(new Pageable(0, 10, "chMdfe,ASC", 0));
    const [totalElements, setTotalElements] = useState<number>(0);
    const [excluindo, setExcluindo] = useState<boolean>(false);
    const [idConta] = useState<number | null>(queryParameters.has('idConta') ? parseInt(queryParameters.get('idConta') || '0') : null);

    const { msgSucesso, msgErro, msgAlerta } = useToastContext();

    const fileUploadRef = useRef<FileUpload>(null);

    const defaultFilters: DataTableFilterMeta = {
        condutor: {
            constraints: [
                {
                    value: '',
                    matchMode: FilterMatchMode.STARTS_WITH,
                }
            ],
            operator: FilterOperator.AND
        },
    }

    const filterContext = useFilter();
    const screen = "mdfe";
    const [filters, setFilters] = useState<DataTableFilterMeta>(filterContext.filters[screen] ? filterContext.filters[screen] : defaultFilters);

    const onPage = (event: DataTableStateEvent) => {

        setPageable({ ...pageable, page: event.page !== undefined ? event.page : 0, first: event.first });
        setFiltrando(true);

    };

    useEffect(() => {
        setFiltrando(true);
    }, []);

    useEffect(() => {
        if (filtrando) {
            filterContext.updateFilters(screen, filters);
            mdfeService
                .pesquisar(pageable, filters)
                .then(lista => {
                    setFiltrando(false);
                    setRows(lista.content);
                    setTotalElements(lista.totalElements);
                })
                .catch(err => {
                    setFiltrando(false);
                });
        }
    }, [filtrando, pageable]);

    useEffect(() => {
        setFiltrando(true);
    }, [filters]);

    const handleUpload = (event: FileUploadHandlerEvent) => {
        const files: File[] = event.files;

        const allFileContents: string[] = [];
        let filesProcessed = 0;

        const readFile = (file: File) => {
            const reader = new FileReader();

            reader.onload = (e: ProgressEvent<FileReader>) => {
                const xmlContent = e.target?.result as string;
                allFileContents.push(xmlContent);

                filesProcessed += 1;

                if (filesProcessed === files.length) {
                    mdfeService
                        .importarXml(allFileContents)
                        .then(() => {
                            setFiltrando(true);
                            msgSucesso(tituloPagina, 'XML importado com sucesso!');
                        })
                        .catch(error => {
                            console.error(error);
                            if (error && error.data.message) {
                                msgErro(tituloPagina, error.data.message);
                            } else {
                                msgErro(tituloPagina, 'Ocorreu um erro ao tentar importar o XML, tente novamente!');
                            }

                        });
                    if (fileUploadRef.current) {
                        fileUploadRef.current.clear();
                    }
                }
            };
            reader.readAsText(file);
        };

        files.forEach(file => readFile(file));
    };

    function editar(item: BaseModel) {
        let identificador = item.id ? item.id : item.codigo;
        let url = `editar/${identificador}`;
        if (idConta) {
            url += `?idConta=${idConta}`;
        }
        navigate(`${url}`);
    }

    function remover(item: BaseModel) {
        confirmDialog({
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            message: 'Deseja realmente excluir este registro?',
            header: 'Confirmação',
            icon: 'pi pi-exclamation-triangle',
            accept: () => removerConfirm(item),
        });
    }

    function removerConfirm(item: BaseModel) {
        setExcluindo(true);
        let identificador = item.id ? item.id : item.codigo;
        if (identificador) {

            mdfeService
                .excluir(Number(identificador))
                .then(() => {
                    setExcluindo(false);
                    setFiltrando(true);
                    msgSucesso(tituloPagina, 'Registro removido com sucesso!');
                })
                .catch(error => {
                    setExcluindo(false);
                    console.error(error);
                    if (error && error.data && error.data.message) {
                        msgErro(tituloPagina, error.data.message);
                    } else {
                        msgErro(tituloPagina, 'Ocorreu um erro ao tentar remover o registro, tente novamente!');
                    }

                });
        }
    }

    const header = <div style={{ display: "flex", justifyContent: "space-between", textAlign: 'center', alignContent: "center" }}>
        <div></div>
        <span style={{ textAlign: 'center', alignSelf: "center", position: 'relative' }}>{tituloPagina}</span>
        <div >
            <FileUpload
                ref={fileUploadRef}
                mode="basic"
                accept="text/xml"
                maxFileSize={1000000}
                customUpload
                uploadHandler={handleUpload}
                auto
                chooseLabel="Importar XML"
                multiple />
        </div>
    </div>

    return (
        <Base tituloPagina={tituloPagina}>
            <DataTable
                header={header}
                value={rows} lazy dataKey="id" paginator
                first={pageable.first} rows={10} totalRecords={totalElements} onPage={onPage}
                loading={filtrando} tableStyle={{ minWidth: '75rem' }}
                filters={filters} onFilter={(e) => setFilters(e.filters)}
                emptyMessage="Nenhum registro encontrado"
            >

                <Column field="chMdfe" header="Conhecimento MDFE" />
                <Column field="listVeicTracao.0.listCondutor.0.xnome" header="Condutor" filter showFilterOperator={false} showAddButton={false} filterPlaceholder="Escreva o filtro"
                    filterMatchModeOptions={TextMatchModeOptions} filterApply={filterApplyTemplate} filterField="condutor" />

                <Column key="colOpcoes" style={{ whiteSpace: 'nowrap', width: '7em' }}
                    body={(item) => <div>
                        <Button type="button" icon={<FontAwesomeIcon icon="edit" />} className="ui-button-info" onClick={() => editar(item)} />
                        <Button type="button" icon={<FontAwesomeIcon icon="remove" />} className="ui-button-danger" style={{ marginLeft: 8 }} onClick={() => remover(item)} />
                    </div>}
                />
            </DataTable>
        </Base>
    )
}